import { useState, useEffect, type ElementType } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Package,
  FileText,
  Users,
  Boxes,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Menu,
} from "lucide-react";
import { useTranslation } from "@/services/i18n/client";
import { route, RouteKeys } from "@/services/routing/routes";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "@/components/navigation/language-switcher";
import { UserMenu } from "@/features/auth/components/user-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@heycircle/ui/sheet";
import { Button } from "@heycircle/ui/button";

const STORAGE_KEY = "admin-sidebar-collapsed";

interface NavItem {
  key: string;
  icon: ElementType;
  href: string;
}

const navItems: NavItem[] = [
  {
    key: "nav.adminDashboard",
    icon: LayoutDashboard,
    href: route(RouteKeys.ADMIN_DASHBOARD),
  },
  {
    key: "nav.companies",
    icon: Building2,
    href: route(RouteKeys.ADMIN_COMPANIES),
  },
  {
    key: "nav.packages",
    icon: Package,
    href: route(RouteKeys.ADMIN_PACKAGES),
  },
  {
    key: "nav.adminContracts",
    icon: FileText,
    href: route(RouteKeys.ADMIN_CONTRACTS),
  },
  {
    key: "nav.users",
    icon: Users,
    href: route(RouteKeys.ADMIN_USERS),
  },
  {
    key: "sidebar.packagingTypes",
    icon: Boxes,
    href: route(RouteKeys.ADMIN_PACKAGING_TYPES),
  },
  {
    key: "nav.monitoring",
    icon: BarChart3,
    href: route(RouteKeys.ADMIN_MONITORING),
  },
];

function SidebarNavItem({
  item,
  isActive,
  isCollapsed,
  label,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
  label: string;
  onClick?: () => void;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      title={isCollapsed ? label : undefined}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
        isActive
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:text-foreground hover:bg-accent",
        isCollapsed && "justify-center px-2"
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!isCollapsed && <span>{label}</span>}
    </Link>
  );
}

export function AdminSidebar() {
  const { t } = useTranslation("common");
  const { t: tAdmin } = useTranslation("admin-panel-home");
  const location = useLocation();

  // Note: The i18next-cli extractor uses static analysis and cannot discover
  // keys from dynamic calls. We use a static navLabels lookup map here so the
  // extractor can see each key explicitly.
  const navLabels: Record<string, string> = {
    "nav.adminDashboard": t("nav.adminDashboard"),
    "nav.companies": t("nav.companies"),
    "nav.packages": t("nav.packages"),
    "nav.adminContracts": t("nav.adminContracts"),
    "nav.users": t("nav.users"),
    "sidebar.packagingTypes": tAdmin("sidebar.packagingTypes"),
    "nav.monitoring": t("nav.monitoring"),
  };

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isCollapsed));
    } catch {
      // localStorage may be unavailable in some environments
    }
  }, [isCollapsed]);

  const toggleCollapsed = () => setIsCollapsed((prev) => !prev);

  return (
    <>
      {/* Desktop sidebar — hidden on mobile */}
      <aside
        className={cn(
          "hidden lg:flex flex-col border-r bg-background min-h-screen transition-all duration-200 shrink-0",
          isCollapsed ? "w-16" : "w-64"
        )}
      >
        {/* Header */}
        <div
          className={cn(
            "flex items-center border-b p-4",
            isCollapsed ? "justify-center" : "justify-between"
          )}
        >
          {isCollapsed ? (
            <span className="font-bold text-sm">{t("appInitials")}</span>
          ) : (
            <div>
              <span className="font-bold text-lg">{t("appName")}</span>
              <div className="text-xs text-muted-foreground font-medium mt-0.5">
                {tAdmin("sidebar.title")}
              </div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 flex flex-col gap-1 p-2 pt-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            const label = navLabels[item.key] ?? item.key;
            return (
              <SidebarNavItem
                key={item.href}
                item={item}
                isActive={isActive}
                isCollapsed={isCollapsed}
                label={label}
              />
            );
          })}
        </nav>

        {/* Bottom: language switcher, user menu, collapse toggle */}
        <div className="border-t p-2 space-y-2">
          {!isCollapsed && (
            <div className="px-1">
              <LanguageSwitcher />
            </div>
          )}
          {!isCollapsed && (
            <div className="px-1">
              <UserMenu />
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className={cn("w-full", isCollapsed && "px-2")}
            title={isCollapsed ? tAdmin("sidebar.expanded") : tAdmin("sidebar.collapsed")}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <div className="flex items-center gap-2 w-full">
                <ChevronLeft className="h-4 w-4" />
                <span className="text-xs text-muted-foreground">{tAdmin("sidebar.collapsed")}</span>
              </div>
            )}
          </Button>
        </div>
      </aside>

      {/* Mobile trigger button — shown only below lg */}
      <div className="lg:hidden fixed top-3 left-3 z-50">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm">
              <Menu className="h-5 w-5" />
              <span className="sr-only">{t("toggleMenu")}</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] sm:w-[320px]">
            <SheetHeader>
              <SheetTitle>
                <div>
                  <span className="font-bold text-lg">{t("appName")}</span>
                  <div className="text-xs text-muted-foreground font-medium mt-0.5">
                    {tAdmin("sidebar.title")}
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col gap-1 mt-6">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                const label = navLabels[item.key] ?? item.key;
                return (
                  <SidebarNavItem
                    key={item.href}
                    item={item}
                    isActive={isActive}
                    isCollapsed={false}
                    label={label}
                    onClick={() => setMobileOpen(false)}
                  />
                );
              })}
              <div className="mt-4 pt-4 border-t space-y-2">
                <LanguageSwitcher />
                <UserMenu />
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
